// Fitted shapes as stored in the edit log (op shape.set): a pole or a surface fitted to the points of
// one part label of an object type, in the world frame of the type's reference instance (ref) before
// its fit F, so it is drawn at F_ref · shape and follows later fits. Knots are uniform clamped cubic,
// given by the control point counts.
//
// A one-off shape (of '', fitted to a selection in the editor) belongs to its scan instead: ref is
// 'f:<frame id>', the source frame most of its points came from, and it is kept in that frame's file
// coordinates, drawn at T · E(frame) (the scan's alignment, then the frame's in the alignment tree), so
// it follows when the frame is aligned later.
import type { PidSet } from '../edit/pidset.ts';

/** A tube: centreline control points (packed xyz, nc = c.length / 3) and radius control values (1: constant). */
export interface PolePiece { c: number[]; r: number[]; rms: number; n: number }

/**
 * A slab: mid-surface w = S(a, b) over the plane through o with axes u, v (normal u × v); (a, b) over
 * the domain ab = [a0, a1, b0, b1] map to [0, 1]² for the heights h (nu x nv, h[i * nv + j]). t is the
 * thickness (the faces are at ± t / 2). mask: the occupied cells of a w x h grid over the domain.
 */
export interface SurfacePiece {
  o: number[]; u: number[]; v: number[]; ab: number[];
  nu: number; nv: number; h: number[]; t: number;
  mask: { w: number; h: number; set: PidSet };
  rms: number; n: number;
}

export type ShapeKind = 'pole' | 'surface';

export interface ShapeDoc {
  kind: ShapeKind;
  /** The instance whose frame the shape is in (a one-off: 'f:<frame id>'). */
  ref: string;
  /** Labelled points it was fitted to (to tell when the label has changed since). */
  n: number;
  /** The parameters it was fitted with. */
  p: Record<string, number | string>;
  pieces: (PolePiece | SurfacePiece)[];
}

export const MAX_PIECES = 64;

const fin = (x: unknown, lim = 1e5): x is number => typeof x === 'number' && Number.isFinite(x) && Math.abs(x) <= lim;
const nums = (a: unknown, len: number | ((n: number) => boolean), lim?: number): a is number[] =>
  Array.isArray(a) && (typeof len === 'number' ? a.length === len : len(a.length)) && a.every(x => fin(x, lim));
const int = (x: unknown, lo: number, hi: number): x is number => Number.isInteger(x) && (x as number) >= lo && (x as number) <= hi;

function isPole(x: Record<string, unknown>) {
  return nums(x.c, n => n % 3 === 0 && n >= 12 && n <= 3 * 64) && nums(x.r, n => n >= 1 && n <= 8) && (x.r as number[]).every(r => r >= 0)
    && fin(x.rms) && int(x.n, 0, 1e9);
}

function isSurface(x: Record<string, unknown>) {
  if (!int(x.nu, 4, 48) || !int(x.nv, 4, 48)) return false;
  const m = x.mask as Record<string, unknown> | undefined, s = m?.set as Record<string, unknown> | undefined;
  return nums(x.o, 3) && nums(x.u, 3, 2) && nums(x.v, 3, 2) && nums(x.ab, 4) && nums(x.h, (x.nu as number) * (x.nv as number))
    && fin(x.t) && (x.t as number) >= 0 && fin(x.rms) && int(x.n, 0, 1e9)
    && !!m && int(m.w, 1, 256) && int(m.h, 1, 256) && !!s && s.enc === 'rle1' && int(s.n, 0, 65536) && typeof s.b64 === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(s.b64 as string);
}

export function isShapeDoc(x: unknown): x is ShapeDoc {
  const d = x as Record<string, unknown>;
  if (!d || typeof d !== 'object' || (d.kind !== 'pole' && d.kind !== 'surface') || typeof d.ref !== 'string' || !d.ref || d.ref.length > 200) return false;
  if (!int(d.n, 0, 1e9) || !d.p || typeof d.p !== 'object' || Array.isArray(d.p)) return false;
  const p = Object.entries(d.p as object);
  if (p.length > 16 || !p.every(([k, v]) => k.length <= 32 && (fin(v, 1e9) || (typeof v === 'string' && v.length <= 200)))) return false;
  if (!Array.isArray(d.pieces) || !d.pieces.length || d.pieces.length > MAX_PIECES) return false;
  return d.pieces.every(q => !!q && typeof q === 'object' && (d.kind === 'pole' ? isPole(q) : isSurface(q)));
}

const r4 = (x: number) => Math.round(x * 1e4) / 1e4, r6 = (x: number) => Math.round(x * 1e6) / 1e6;

/** Coordinates to 0.1 mm, axes to 1e-6: compact ops. */
export function roundDoc(d: ShapeDoc): ShapeDoc {
  return {
    ...d,
    pieces: d.pieces.map(q => 'c' in q
      ? { c: q.c.map(r4), r: q.r.map(r4), rms: r4(q.rms), n: q.n }
      : { ...q, o: q.o.map(r4), u: q.u.map(r6), v: q.v.map(r6), ab: q.ab.map(r4), h: q.h.map(r4), t: r4(q.t), rms: r4(q.rms) }),
  };
}

export const isPolePiece = (q: PolePiece | SurfacePiece): q is PolePiece => 'c' in q;

/**
 * A plane (op plane.set, a one-off in the scan like a one-off shape): the rectangle over the plane through o with
 * unit axes u, v (normal u × v) spanning (a, b) in ab = [a0, a1, b0, b1] (m), kept in the file coordinates of its
 * frame ref 'f:<frame id>' (0: the scan's own) so it follows that frame's alignment. n: the points it was fitted to.
 */
export interface PlaneDoc { ref: string; o: number[]; u: number[]; v: number[]; ab: number[]; n: number }

export function isPlaneDoc(x: unknown): x is PlaneDoc {
  const d = x as Record<string, unknown>;
  if (!d || typeof d !== 'object' || Array.isArray(d) || !Object.keys(d).every(k => ['ref', 'o', 'u', 'v', 'ab', 'n'].includes(k))) return false;
  if (typeof d.ref !== 'string' || !/^f:\d{1,10}$/.test(d.ref) || !int(d.n, 0, 1e9)) return false;
  if (!nums(d.o, 3) || !nums(d.u, 3, 2) || !nums(d.v, 3, 2) || !nums(d.ab, 4)) return false;
  const [a0, a1, b0, b1] = d.ab as number[];
  return a1 > a0 && b1 > b0;
}

/** As roundDoc: coordinates to 0.1 mm, axes to 1e-6. */
export const roundPlane = (d: PlaneDoc): PlaneDoc => ({ ref: d.ref, o: d.o.map(r4), u: d.u.map(r6), v: d.v.map(r6), ab: d.ab.map(r4), n: d.n });
